import {
  type EnvironmentProviders,
  InjectionToken,
  inject,
} from '@angular/core';
import type { BusEvent } from './bus-event';
import { provideEventTap } from './event-tap';

/**
 * A read-only analytics sink for the event bus.
 *
 * Implementations forward every dispatched {@link BusEvent} (its `type` plus
 * payload) to some external analytics backend. The sink is a pure *consumer* of
 * the ordered stream: it never dispatches and must never throw in a way that
 * tears down the shared subscription (forward defensively).
 *
 * The core (`@tvd/cdk/events`) deliberately knows nothing about any concrete
 * backend. A concrete sink (e.g. one forwarding to PostHog) lives in the lib
 * that owns that dependency and is wired in via {@link BUS_ANALYTICS_SINK}; this
 * keeps the build graph one-directional (data-access → cdk, never the reverse).
 */
export interface BusAnalyticsSink {
  /** Forward a single dispatched event to the analytics backend. */
  forward(event: BusEvent): void;
}

/**
 * DI token an app binds to its concrete {@link BusAnalyticsSink}.
 *
 * Off by default: nothing in the core provides it. `provideEventBusAnalytics`
 * only activates forwarding when a sink is bound to this token.
 */
export const BUS_ANALYTICS_SINK = new InjectionToken<BusAnalyticsSink>(
  'BUS_ANALYTICS_SINK',
);

/**
 * Opt-in: forward every dispatched event to the {@link BusAnalyticsSink} bound
 * to {@link BUS_ANALYTICS_SINK}.
 *
 * Off by default — nothing in the core binds the token, so without a binding the
 * initializer subscribes to nothing. Bind the token (e.g. `useExisting` an
 * injectable adapter) and call this to activate forwarding:
 * ```ts
 * { provide: BUS_ANALYTICS_SINK, useExisting: PostHogBusSink },
 * provideEventBusAnalytics(),
 * ```
 * The read-only subscription never dispatches and never `error`/`complete`s the
 * immortal root `Subject`; its teardown is tied to the environment injector's
 * `DestroyRef` (see `provideEventTap`).
 */
export function provideEventBusAnalytics(): EnvironmentProviders {
  return provideEventTap(() => {
    const sink = inject(BUS_ANALYTICS_SINK, { optional: true });
    return sink ? (event) => sink.forward(event) : null;
  });
}
